import { useState, useEffect, type ReactNode } from 'react';
import { useWallet } from '../WalletContext.js';
import { formatTokenAmount, shortenAddress, getChainConfig } from '@tokenstreaming/shared';
import { APP_VERSION } from '../../../version.js';

interface Props {
  onSend: () => void;
  onReceive: () => void;
  onHistory: () => void;
  onCreateCall: () => void;
  onJoinCall: () => void;
  onLock: () => void;
  onBackup: () => void;
  onSettings: () => void;
}

export function WalletHome({ onSend, onReceive, onHistory, onCreateCall, onJoinCall, onLock, onBackup, onSettings }: Props): ReactNode {
  const { status, balances, chainId, refreshBalances } = useWallet();
  const [refreshing, setRefreshing] = useState(false);

  const chain = getChainConfig(chainId);
  const address = status.state === 'unlocked' ? status.address : '';

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshBalances();
    setRefreshing(false);
  };

  const handleReload = async () => {
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map(name => caches.delete(name)));
    }
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
    }
    window.location.reload();
  };

  useEffect(() => {
    void refreshBalances();
  }, [refreshBalances]);

  return (
    <div className="screen">
      <div className="screen-header">
        <div className="header-left">
          <span className="network-badge">{chain.name}</span>
        </div>
        <div className="header-right">
          <button className="btn-ghost btn-sm" onClick={onLock}>Lock</button>
        </div>
      </div>

      <div className="screen-body">
        <div className="wallet-address" onClick={() => void navigator.clipboard.writeText(address)}>
          <span className="address-text">{shortenAddress(address)}</span>
          <span className="copy-hint">tap to copy</span>
        </div>

        <div className="balance-card">
          <div className="balance-amount">
            {formatTokenAmount(balances.native, chain.nativeDecimals)}
          </div>
          <div className="balance-symbol">{chain.nativeSymbol}</div>
          <button className="btn-ghost btn-sm refresh-btn" onClick={() => void handleRefresh()} disabled={refreshing}>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {balances.tokens.length > 0 && (
          <div className="token-list">
            {balances.tokens.map(token => (
              <div key={token.address} className="token-row">
                <div className="token-info">
                  <span className="token-symbol">{token.symbol}</span>
                  <span className="token-name">{token.name}</span>
                </div>
                <div className="token-balance">
                  {formatTokenAmount(token.balance, token.decimals)}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="action-grid">
          <button className="action-btn" onClick={onSend}>
            <span className="action-icon">&#x2191;</span>
            <span>Send</span>
          </button>
          <button className="action-btn" onClick={onReceive}>
            <span className="action-icon">&#x2193;</span>
            <span>Receive</span>
          </button>
          <button className="action-btn" onClick={onHistory}>
            <span className="action-icon">&#x1F4CB;</span>
            <span>History</span>
          </button>
        </div>

        <div className="streaming-actions">
          <button className="btn-primary btn-full" onClick={onCreateCall}>Start Streaming</button>
          <button className="btn-secondary btn-full" onClick={onJoinCall}>Join Stream</button>
        </div>

        {balances.lastUpdated > 0 && (
          <p className="text-muted text-sm text-center">
            Last updated: {new Date(balances.lastUpdated).toLocaleTimeString()}
          </p>
        )}

        <div className="version-reload">
          <span className="version-label">v{APP_VERSION}</span>
          <button className="btn-ghost btn-xs" onClick={onSettings}>Settings</button>
          <button className="btn-ghost btn-xs" onClick={onBackup}>Backup</button>
          <button className="btn-ghost btn-xs" onClick={() => void handleReload()}>Reload</button>
        </div>
      </div>
    </div>
  );
}
